import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

// Services
import { useDashboard } from '../../../services/dashboard/dashboardQueries';
import { useExportMutation } from '../../../services/export/exportQueries';
import { useDashboardFilters } from '../../../services/dashboard-filters/dashboardFiltersQueries';
import { useDashboardFiltersOptimistic } from '../../../services/dashboard-filters/dashboardFiltersOptimistic';

// Hooks
import { useContexte } from '../../../hooks/useContexte';
import { useFormPanelContext } from '../../../store/formPanel/FormPanelContext';
import useToast from '../../molecules/Toast/useToast';

// Domains
import useDashboardExport from '../../../domain/dashboardExport/useDashboardExport';
import useDashboardEditForm from '../../../domain/dashboardEditForm/useDashboardEditForm';
import { addIdsToEditFormUrls } from '../../../domain/dashboardEditForm/dashboardEditFormUseCase';
import useDashboardAction from '../../../domain/dashboardAction/useDashboardAction';
import {
  completeDashboardFiltersForPost,
  hasDashboardFilters,
  isDashboardReloadingAfterFilters,
} from '../../../domain/dashboardFilters/dashboardFiltersUseCase';

// Store
import { Popover } from '../../../store/popover/PopoverContext';
import { DashboardActionProvider } from '../../../store/dashboardAction/DashboardActionContext';

// Components
import DashboardTab from '../../atoms/DashboardTab/DashboardTab';
import { Skeleton } from '../../atoms/Skeleton/Skeleton';
import FloatingMenu, {
  FloatingMenuGroups,
  FloatingMenuGroup,
} from '../../atoms/FloatingMenu/FloatingMenu';
import Button from '../../atoms/Button/Button';
import {
  PopoverContent,
  PopoverTrigger,
} from '../../atoms/Popover/Popover';
import CustomVueListGroup, {
  CustomVueListGroupList,
  CustomVueListGroupTitle,
} from '../CustomVueListGroup/CustomVueListGroup';
import ColumnsVisibility from '../../molecules/ColumnsVisibility/ColumnsVisibility';
import SearchField from '../../molecules/SearchField/SearchField';
import SearchGroupList from '../../molecules/SearchGroupList/SearchGroupList';
import SearchListItem from '../../molecules/SearchListItem/SearchListItem';
import LabelWithIconAndColor from '../../molecules/LabelWithIconAndColor/LabelWithIconAndColor';
import HorizontalList from '../../atoms/HorizontalList/HorizontalList';
import DashboardActionConfirmModal from '../../molecules/DashboardActionConfirmModal/DashboardActionConfirmModal';
import DashboardFilters from '../DashboardFilters/DashboardFilters';
import DashboardCardsSkeleton from '../DashboardCardsSkeleton/DashboardCardsSkeleton';
import DashboardTabCards from '../DashboardTabCards/DashboardTabCards';
// Styles
import styles from './DashboardContent.module.css';

/**
 * DashboardContent component - Renders dashboard tabs and fieldsets
 * Always uses useDashboard to fetch data from API
 * @param {object} props - Component props
 * @param {ApiPage} [props.page] - Page configuration with path
 * @returns {JSX.Element | null}
 */
function DashboardContent({ page }) {
  const { t } = useTranslation();
  const { notify } = useToast();
  const [activeTab, setActiveTab] = useState(null);

  const contexte = useContexte();

  // Always use useDashboard with page.path and contexte
  const {
    data: dashboardData,
    isLoading,
    isFetching: isDashboardFetching,
    dataUpdatedAt: dashboardUpdatedAt,
  } = useDashboard(page?.path, contexte, {
    enabled: !!page?.path,
  });

  // Services - Dashboard Filters (borrowed from the source list; POST then /dashboard reload)
  const {
    data: dashboardFilters,
    isLoading: isDashboardFiltersLoading,
  } = useDashboardFilters({ path: page?.path, contexte }, { enabled: !!page?.path });

  const {
    mutate: mutateDashboardFilters,
    isPending: isDashboardFiltersPending,
    submittedAt: dashboardFiltersSubmittedAt,
  } = useDashboardFiltersOptimistic(page?.path, {}, contexte);

  const isReloadingAfterFilters = isDashboardReloadingAfterFilters({
    isFiltersPending: isDashboardFiltersPending,
    isDashboardFetching,
    dashboardUpdatedAt,
    filtersSubmittedAt: dashboardFiltersSubmittedAt,
  });

  /**
   * @param {Partial<ApiManagerDashboardFiltersCreate>} partial - Changed part of the filters
   * @param {{ onSuccess?: () => void }} [options] - Mutation options
   */
  const handleDashboardFiltersSubmit = (partial, options) => {
    mutateDashboardFilters(completeDashboardFiltersForPost(partial, dashboardFilters), options);
  };

  const { handleOpenModal } = useFormPanelContext();

  // Domain - Dashboard Export
  const {
    popoverExportIsOpen,
    setPopoverExportIsOpen,
    searchValue: exportSearchValue,
    setSearchValue: setExportSearchValue,
    selectedFormatFilter,
    setSelectedFormatFilter,
    exportUrlsGrouped,
    optGroupsList,
  } = useDashboardExport({ exportUrls: dashboardData?.exportUrls });

  // Domain - Dashboard Edit Form
  const {
    popoverEditFormIsOpen,
    setPopoverEditFormIsOpen,
    searchValue: editFormSearchValue,
    setSearchValue: setEditFormSearchValue,
    editFormUrlsGrouped,
  } = useDashboardEditForm({ editFormUrls: dashboardData?.editFormUrls });

  const dashboardAction = useDashboardAction();

  /**
   * Handle click on "Edit" button
   * If there's only one editFormUrl, open the form directly
   * Otherwise, open the popover with the list
   */
  const handleEditFormClick = () => {
    const urls = dashboardData?.editFormUrls;
    if (urls?.length === 1) {
      // just add an id to the url to respect the type DashboardEditFormUrlsWithId
      const withIds = addIdsToEditFormUrls(urls);
      handleOpenModal(withIds[0].url);
    } else {
      setPopoverEditFormIsOpen(!popoverEditFormIsOpen);
    }
  };

  const {
    mutate: mutateExport,
  } = useExportMutation({
    onSuccess: (data) => {
      window.open(data.url, '_blank');
    },
    onError: (error) => {
      notify({
        message: error?.message || t('toast.export.error', 'Une erreur est survenue lors de l\'export'),
        type: 'alert',
      });
    },
  });

  /**
   * @param {string} url - The URL to export
   */
  const handleExport = (url) => {
    mutateExport({ url, selectedIds: [] });
  };

  // Set default active tab from tabs data (only on first load)
  // TODO : Not sure the useEffect is a good pattern here.
  useEffect(() => {
    if (dashboardData?.tabs && dashboardData.tabs.length > 0 && activeTab === null) {
      setActiveTab(dashboardData.tabs[0].id);
    }
  }, [dashboardData]);

  if (isLoading) {
    return (
      <div className={styles['dashboard-content']}>
        {/* Skeletons for tabs */}
        <nav className={styles['dashboard-tabs']}>
          <Skeleton shape="rectangle" width="120px" height="32px" />
          <Skeleton shape="rectangle" width="120px" height="32px" />
          <Skeleton shape="rectangle" width="120px" height="32px" />
        </nav>

        <div className={styles['dashboard-content-tab']}>
          {/* Skeleton for subLegend */}
          <Skeleton shape="rectangle" width="40%" height="20px" className={styles['dashboard-content-sublegend']} />

          {/* Skeletons for cards */}
          <DashboardCardsSkeleton />
        </div>
      </div>
    );
  }

  if (!dashboardData || !dashboardData.tabs || dashboardData.tabs.length === 0) {
    return null;
  }

  const { tabs } = /** @type {ApiDashboardResponse} */ (dashboardData);
  const currentTab = tabs.find((tab) => tab.id === activeTab);

  /**
   * Handle tab click
   * @param {number} tabId - ID of the clicked tab
   */
  const handleTabClick = (tabId) => {
    setActiveTab(tabId);
  };

  return (
    <>
      <DashboardActionProvider value={dashboardAction}>
        <div className={styles['dashboard-content']}>
          {tabs.length > 1 && (
            <nav className={styles['dashboard-tabs']}>
              {tabs.map((tab) => (
                <DashboardTab
                  key={tab.id}
                  isActive={activeTab === tab.id}
                  onClick={() => handleTabClick(tab.id)}
                >
                  {tab.legend}
                </DashboardTab>
              ))}
            </nav>
          )}

          {hasDashboardFilters(dashboardFilters) && (
            <DashboardFilters
              dashboardFilters={dashboardFilters}
              path={page?.path}
              contexte={contexte}
              isLoading={isDashboardFiltersLoading}
              isSubmitting={isDashboardFiltersPending}
              onSubmit={handleDashboardFiltersSubmit}
              handleOpenModal={handleOpenModal}
            />
          )}

          {currentTab && (
            <div className={styles['dashboard-content-tab']}>
              {currentTab.subLegend && (
                <p className={styles['dashboard-content-sublegend']}>{currentTab.subLegend}</p>
              )}
              <DashboardTabCards
                fieldsets={currentTab.fieldsets}
                isReloading={isReloadingAfterFilters}
              />
            </div>
          )}
        </div>
        <DashboardActionConfirmModal
          isOpen={dashboardAction.confirmIsOpen}
          onClose={dashboardAction.closeConfirm}
          onConfirm={dashboardAction.confirmAction}
          message={dashboardAction.confirmMessage}
          isLoading={dashboardAction.isPending}
        />
      </DashboardActionProvider>

      <FloatingMenu>
        <FloatingMenuGroups>
          <FloatingMenuGroup>
            {dashboardData?.exportUrls && dashboardData.exportUrls.length > 0 && (
              <Popover
                open={popoverExportIsOpen}
                onOpenChange={setPopoverExportIsOpen}
                placement="top"
              >
                <PopoverTrigger>
                  <Button
                    color="no-background"
                    size="small"
                    iconPosition="right"
                    icon="export"
                    onClick={() => setPopoverExportIsOpen(!popoverExportIsOpen)}
                  >
                    {t('table.export')}
                  </Button>
                </PopoverTrigger>
                <PopoverContent>
                  <CustomVueListGroup variant="wide">
                    <CustomVueListGroupList>
                      <CustomVueListGroupTitle>{t('table.export')}</CustomVueListGroupTitle>
                      {optGroupsList?.length ? (
                        <HorizontalList items={optGroupsList} gap="xs" className="margin-top-s margin-bottom-s">
                          {(option) => {
                            const isSelected = selectedFormatFilter === option;
                            return (
                              <Button
                                key={option}
                                color={isSelected ? 'primary' : 'primary100'}
                                size="small"
                                onClick={() => {
                                  // Toggle filter: if already selected, deselect it
                                  setSelectedFormatFilter(isSelected ? null : option);
                                }}
                              >
                                {option}
                              </Button>
                            );
                          }}
                        </HorizontalList>
                      ) : (
                        <p>{t('table.filters.export.noResult')}</p>
                      )}
                      <ColumnsVisibility
                        search={(
                          <SearchField
                            id="dashboard-export-search"
                            placeholder={t('table.filters.columnsGroupsSearch')}
                            value={exportSearchValue}
                            onChange={(e) => setExportSearchValue(e.target.value)}
                            onFocus={() => {}}
                          />
                        )}
                        options={exportUrlsGrouped}
                        messageNoOptions={t('table.filters.noResult')}
                      >
                        {(key, items) => (
                          <SearchGroupList
                            key={key}
                            hasLabel={!!key}
                            items={items}
                            label={key}
                          >
                            {(/** @type {DashboardExportUrlsWithId} */ item) => (
                              <SearchListItem
                                key={item.id}
                                id={item.id}
                                theme="group"
                                onClick={() => handleExport(item.url)}
                                isSelected={false}
                              >
                                <LabelWithIconAndColor
                                  label={item.lib}
                                  icon={item.icon}
                                  iconColor={item.iconColor}
                                />
                              </SearchListItem>
                            )}
                          </SearchGroupList>
                        )}
                      </ColumnsVisibility>
                    </CustomVueListGroupList>
                  </CustomVueListGroup>
                </PopoverContent>
              </Popover>
            )}
            {dashboardData?.editFormUrls && dashboardData.editFormUrls.length > 0 && (
              <Popover
                open={popoverEditFormIsOpen}
                onOpenChange={setPopoverEditFormIsOpen}
                placement="top"
              >
                <PopoverTrigger>
                  <Button
                    color="primary"
                    size="small"
                    onClick={handleEditFormClick}
                  >
                    {t('dashboard.edit')}
                  </Button>
                </PopoverTrigger>
                <PopoverContent>
                  <CustomVueListGroup>
                    <CustomVueListGroupList>
                      <CustomVueListGroupTitle>{t('dashboard.edit')}</CustomVueListGroupTitle>
                      <ColumnsVisibility
                        search={(
                          <SearchField
                            id="dashboard-edit-form-search"
                            placeholder={t('table.filters.columnsGroupsSearch')}
                            value={editFormSearchValue}
                            onChange={(e) => setEditFormSearchValue(e.target.value)}
                            onFocus={() => {}}
                            className="margin-top-s"
                          />
                        )}
                        options={editFormUrlsGrouped}
                        messageNoOptions={t('table.filters.noResult')}
                      >
                        {(key, items) => (
                          <SearchGroupList
                            key={key}
                            hasLabel={!!key}
                            items={items}
                            label={key}
                          >
                            {(/** @type {DashboardEditFormUrlsWithId} */ item) => (
                              <SearchListItem
                                key={item.id}
                                id={item.id}
                                theme="group"
                                onClick={() => {
                                  handleOpenModal(item.url);
                                }}
                                isSelected={false}
                              >
                                <LabelWithIconAndColor
                                  label={item.lib}
                                  icon={item.icon}
                                  iconColor={item.iconColor}
                                />
                              </SearchListItem>
                            )}
                          </SearchGroupList>
                        )}
                      </ColumnsVisibility>
                    </CustomVueListGroupList>
                  </CustomVueListGroup>
                </PopoverContent>
              </Popover>
            )}
          </FloatingMenuGroup>
        </FloatingMenuGroups>
      </FloatingMenu>
    </>
  );
}

export default DashboardContent;
